import { Injectable, signal, computed, effect } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'system';

const KEYS = {
  theme: 'tt.ui.theme',
  sidebarCollapsed: 'tt.ui.sidebarCollapsed',
  expandedSection: 'tt.ui.expandedSection',
  favorites: 'tt.ui.favorites',
  recent: 'tt.ui.recent'
} as const;

@Injectable({ providedIn: 'root' })
export class UiPreferencesService {
  readonly themeMode = signal<ThemeMode>(this.readTheme());
  readonly sidebarCollapsed = signal(this.readBool(KEYS.sidebarCollapsed, false));
  readonly expandedSectionId = signal<string | null>(this.readString(KEYS.expandedSection) ?? 'dashboard');
  readonly favorites = signal<string[]>(this.readJson<string[]>(KEYS.favorites, []));
  readonly recent = signal<string[]>(this.readJson<string[]>(KEYS.recent, []));

  /** @deprecated dual-nav leftovers — mapped to sidebarCollapsed */
  readonly panelCollapsed = this.sidebarCollapsed;
  readonly panelPinned = signal(true);
  readonly activeSectionId = this.expandedSectionId;

  readonly resolvedTheme = computed<'light' | 'dark'>(() => {
    const mode = this.themeMode();
    if (mode === 'system') {
      return typeof window !== 'undefined' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return mode;
  });

  readonly isDark = computed(() => this.resolvedTheme() === 'dark');

  constructor() {
    effect(() => {
      const theme = this.resolvedTheme();
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem(KEYS.theme, this.themeMode());
    });

    effect(() => {
      localStorage.setItem(KEYS.sidebarCollapsed, String(this.sidebarCollapsed()));
    });

    effect(() => {
      const id = this.expandedSectionId();
      if (id) localStorage.setItem(KEYS.expandedSection, id);
      else localStorage.removeItem(KEYS.expandedSection);
    });

    effect(() => {
      localStorage.setItem(KEYS.favorites, JSON.stringify(this.favorites()));
    });

    effect(() => {
      localStorage.setItem(KEYS.recent, JSON.stringify(this.recent()));
    });

    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        if (this.themeMode() === 'system') {
          document.documentElement.setAttribute('data-theme', this.resolvedTheme());
        }
      });
    }
  }

  setTheme(mode: ThemeMode): void {
    this.themeMode.set(mode);
  }

  cycleTheme(): void {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    const i = order.indexOf(this.themeMode());
    this.themeMode.set(order[(i + 1) % order.length]);
  }

  toggleSidebarCollapsed(): void {
    this.sidebarCollapsed.update((v) => !v);
  }

  /** Accordion: only one section open at a time */
  setExpandedSection(id: string | null): void {
    this.expandedSectionId.set(id);
  }

  toggleExpandedSection(id: string): void {
    const current = this.expandedSectionId();
    this.expandedSectionId.set(current === id ? null : id);
  }

  setActiveSection(id: string | null): void {
    this.setExpandedSection(id);
  }

  togglePanelCollapsed(): void {
    this.toggleSidebarCollapsed();
  }

  togglePanelPinned(): void {
    this.panelPinned.update((v) => !v);
  }

  toggleFavorite(route: string): void {
    this.favorites.update((list) =>
      list.includes(route) ? list.filter((r) => r !== route) : [route, ...list].slice(0, 12)
    );
  }

  isFavorite(route: string): boolean {
    return this.favorites().includes(route);
  }

  pushRecent(route: string, label: string): void {
    const entry = `${route}|${label}`;
    this.recent.update((list) => [entry, ...list.filter((x) => !x.startsWith(route + '|'))].slice(0, 8));
  }

  recentItems(): { route: string; label: string }[] {
    return this.recent()
      .map((raw) => {
        const i = raw.indexOf('|');
        if (i < 0) return null;
        return { route: raw.slice(0, i), label: raw.slice(i + 1) };
      })
      .filter((x): x is { route: string; label: string } => !!x);
  }

  private readTheme(): ThemeMode {
    const v = localStorage.getItem(KEYS.theme);
    return v === 'dark' || v === 'light' || v === 'system' ? v : 'light';
  }

  private readBool(key: string, fallback: boolean): boolean {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v === 'true';
  }

  private readString(key: string): string | null {
    return localStorage.getItem(key);
  }

  private readJson<T>(key: string, fallback: T): T {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
      return fallback;
    }
  }
}
